import { useEffect, useRef, useState } from "react";
import type { Position } from "@auto-pools/core";
import { api, ApiError } from "../api/client";

/**
 * "Take manual control" pauses the run and hands the stop back to the operator (design 47, section
 * 7 / plan Phase 4 decision 7). It never removes the stop, and never touches an in-flight close.
 */
export function AutopilotTakeControlPopover({
  position,
  onClose,
  onReleased,
}: {
  position: Position;
  onClose: () => void;
  onReleased: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    keep.current?.focus();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  async function submit() {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api(`/autopilot/release/${encodeURIComponent(position.id)}`, {
        method: "POST",
        body: {},
        sensitive: true,
      });
      onReleased();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : reason instanceof Error
            ? reason.message
            : "Take manual control was not accepted",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <dialog
      ref={dialog}
      className="position-action-modal autopilot-dialog"
      aria-labelledby="autopilot-take-control-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <header className="modal-header">
        <div>
          <h2 id="autopilot-take-control-title">Take manual control?</h2>
          <small>{position.pair}</small>
        </div>
        <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="position-action-body">
        <p>
          Pauses the autopilot and hands this position back to you. The stop stays exactly as it is
          — this only changes who owns it. The autopilot starts a new cycle from a flat state and
          never touches this position again.
        </p>
        {error && (
          <p className="inline-alert warning" role="alert">
            {error}
          </p>
        )}
      </div>
      <footer>
        <button ref={keep} type="button" className="button" onClick={onClose}>
          Keep autopilot control
        </button>
        <button
          type="button"
          className="button danger"
          disabled={pending}
          onClick={() => void submit()}
        >
          {pending ? "Taking control…" : "Take manual control"}
        </button>
      </footer>
    </dialog>
  );
}
